/**
 * TEMPORARY mock backend for `useSyncTimelineData` — used to verify the
 * recent/historic merge bug fixes. Toggle via `USE_MOCK_TIMELINE_DATA` in
 * `useSyncTimelineData.ts`. Delete this file once verification is done.
 *
 * Models two stores:
 *  - "recent" (DynamoDB): only returns rows started within the last 1h.
 *    Treated as the source of truth for fresh data — when recent has a row,
 *    its snapshot reflects the current state of that job. Domain invariant:
 *    only the single most-recent row can be Running.
 *  - "historic" (BigQuery): returns rows started in `[from, now-LAG]`
 *    (i.e. 2 min lag). May contain a snapshot taken WHILE a job was running,
 *    so a row that has since completed can appear here as Running with
 *    null finishedAt/duration.
 *
 * Two scenarios baked in (anchored to NOW at module load):
 *
 *   A) Historic-stale-running boundary (fixes #1 and #2 together):
 *      Row at NOW-25min. Historic returns it as Running with finishedAt
 *      and duration as null and rowsSynced=0 (materialization snapshot
 *      caught it mid-run). Recent returns it as Completed with full data.
 *      Expected: the bar/tooltip shows Completed with the populated
 *      finishedAt, duration, and final rowsSynced — recent wins, and the
 *      null-guard prevents historic's nulls from leaking through.
 *
 *   B) Stale-recent-in-local-state (fix #1):
 *      Row at NOW-45min. The FIRST recent fetch returns it as Running with
 *      rowsSynced=0 (in-flight snapshot captured while the page was open).
 *      Every subsequent recent fetch omits the row (as if DynamoDB has
 *      since updated it out of the queried window, or the throttled
 *      `now - 10m` window no longer reaches it). Historic returns it as
 *      Completed with rowsSynced=5000.
 *      With the fix: the second recent fetch's `replaceRecentInWindow`
 *      evicts the stale row → historic Completed wins. Without the fix
 *      (old `producer` that only upserts) the stale Running row persists
 *      indefinitely.
 *
 * Plus a handful of "normal" rows for visual context.
 */

import {
  EltSyncHistoryStatsResponseFragment,
  FullSyncTrigger,
  OperationMode,
  SyncJobStatus,
} from "@/apollo/types";

const NOW = Date.now();
const MINUTE = 60_000;

// Keep the mock retention generous so we exercise the merge logic, not the
// TTL boundary. The real DynamoDB retention is at least as long as the
// recent fetch window in `useSyncTimelineData` (4h today).
const RECENT_TTL_MS = 6 * 60 * MINUTE; // 6h
const HISTORIC_LAG_MS = 2 * MINUTE; // 2m
const IN_FLIGHT_THRESHOLD_MS = 5 * MINUTE;

const DEFAULT_STREAM = "public.users";

type Stat = EltSyncHistoryStatsResponseFragment["stats"][number];

type TruthRow = {
  /** Offset from NOW in minutes. Positive = past. */
  agoMin: number;
  status: SyncJobStatus;
  rowsSynced: number;
  rowsSyncedMb: number;
  duration: number | undefined; // seconds
  sourceStreamName?: string;
  fullSyncTrigger?: FullSyncTrigger;
  operationMode?: OperationMode;
};

const truth: TruthRow[] = [
  {
    agoMin: 3,
    status: SyncJobStatus.Running,
    rowsSynced: 0,
    rowsSyncedMb: 0,
    duration: undefined,
  }, // in-flight
  {
    agoMin: 10,
    status: SyncJobStatus.Completed,
    rowsSynced: 120,
    rowsSyncedMb: 0.05,
    duration: 8_000 * 60, // 8m
  },
  {
    agoMin: 25,
    status: SyncJobStatus.Completed,
    rowsSynced: 1000,
    rowsSyncedMb: 0.4,
    duration: 5_000 * 60, // 5m
  }, // scenario A (null-clobber)
  {
    agoMin: 35,
    status: SyncJobStatus.Completed,
    rowsSynced: 800,
    rowsSyncedMb: 0.3,
    duration: 4_500 * 60, // 4.5m
  },
  {
    agoMin: 45,
    status: SyncJobStatus.Completed,
    rowsSynced: 5000,
    rowsSyncedMb: 2.0,
    duration: 12_000 * 60, // 12m
  }, // scenario B (stale-recent)
  {
    agoMin: 55,
    status: SyncJobStatus.Completed,
    rowsSynced: 600,
    rowsSyncedMb: 0.2,
    duration: 4_000 * 60, // 4m
  },
  {
    agoMin: 80, // 1h20m ago
    status: SyncJobStatus.Failed,
    rowsSynced: 0,
    rowsSyncedMb: 0,
    duration: 1_500 * 60, // 1.5m
  },
  {
    agoMin: 120, // 2h ago
    status: SyncJobStatus.Completed,
    rowsSynced: 950,
    rowsSyncedMb: 0.35,
    duration: 5_200 * 60, // 5.2m
  },
  {
    agoMin: 180, // 3h ago
    status: SyncJobStatus.Completed,
    rowsSynced: 1100,
    rowsSyncedMb: 0.45,
    duration: 5_800 * 60, // 5.8m
  },
  {
    agoMin: 240, // 4h ago
    status: SyncJobStatus.Completed,
    rowsSynced: 870,
    rowsSyncedMb: 0.32,
    duration: 4_900 * 60,
  },
  {
    agoMin: 6 * 60, // 6h ago
    status: SyncJobStatus.Completed,
    rowsSynced: 740,
    rowsSyncedMb: 0.28,
    duration: 4_700 * 60,
  },
  {
    agoMin: 12 * 60, // 12h ago
    status: SyncJobStatus.Completed,
    rowsSynced: 1300,
    rowsSyncedMb: 0.5,
    duration: 6_500 * 60,
  },
  {
    agoMin: 24 * 60, // 1 day ago
    status: SyncJobStatus.Completed,
    rowsSynced: 1500,
    rowsSyncedMb: 0.6,
    duration: 7_000 * 60,
  },
  {
    agoMin: 48 * 60, // 2 days ago
    status: SyncJobStatus.Completed,
    rowsSynced: 1400,
    rowsSyncedMb: 0.55,
    duration: 6_800 * 60,
  },
  {
    agoMin: 96 * 60, // 4 days ago
    status: SyncJobStatus.Completed,
    rowsSynced: 1200,
    rowsSyncedMb: 0.48,
    duration: 6_100 * 60,
  },
  {
    agoMin: 144 * 60, // 6 days ago
    status: SyncJobStatus.Completed,
    rowsSynced: 1100,
    rowsSyncedMb: 0.44,
    duration: 5_900 * 60, // 5.9m
  },
];

const startedAtFor = (row: TruthRow) =>
  new Date(NOW - row.agoMin * MINUTE).toISOString();

const finishedAtFor = (row: TruthRow) =>
  row.status === SyncJobStatus.Running || row.duration === undefined
    ? undefined
    : new Date(NOW - row.agoMin * MINUTE + row.duration).toISOString();

const toStat = (row: TruthRow, overrides: Partial<Stat> = {}): Stat => ({
  __typename: "EltSyncHistoryStat",
  startedAt: startedAtFor(row),
  finishedAt: finishedAtFor(row),
  duration:
    row.status === SyncJobStatus.Running || row.duration === undefined
      ? undefined
      : row.duration,
  status: row.status,
  sourceStreamName: row.sourceStreamName ?? DEFAULT_STREAM,
  rowsSynced: row.rowsSynced,
  rowsSyncedMb: row.rowsSyncedMb,
  operationMode: row.operationMode ?? OperationMode.Incremental,
  fullSyncTrigger: row.fullSyncTrigger ?? FullSyncTrigger.None,
  ...overrides,
});

const SCENARIO_A_AGO_MIN = 25;
const SCENARIO_B_AGO_MIN = 45;

let recentFetchCount = 0;

function applyFilter(stats: Stat[], sourceStreamName?: string) {
  if (!sourceStreamName) return stats;
  return stats.filter((s) => s.sourceStreamName === sourceStreamName);
}

export function mockFetchRecent(args: {
  from: Date;
  sourceStreamName?: string;
}): EltSyncHistoryStatsResponseFragment {
  const fromMs = args.from.getTime();
  const callIndex = recentFetchCount++;

  const stats: Stat[] = [];
  for (const row of truth) {
    const startedAtMs = NOW - row.agoMin * MINUTE;

    // "DynamoDB" only retains the last hour.
    if (startedAtMs < NOW - RECENT_TTL_MS) continue;
    // Only return rows within the requested window.
    if (startedAtMs < fromMs) continue;

    // Scenario B: stale row only on first fetch, then disappears.
    if (row.agoMin === SCENARIO_B_AGO_MIN) {
      if (callIndex === 0) {
        stats.push(
          toStat(row, {
            status: SyncJobStatus.Running,
            rowsSynced: 0,
            rowsSyncedMb: 0,
            duration: undefined,
            finishedAt: undefined,
          }),
        );
      }
      continue;
    }

    // Scenario A: recent has the fresh, final Completed snapshot. (Historic
    // is the one with a lagged Running snapshot — see mockFetchHistoric.)
    if (row.agoMin === SCENARIO_A_AGO_MIN) {
      stats.push(toStat(row));
      continue;
    }

    // In-flight if very recent (the single Running row per stream).
    if (NOW - startedAtMs < IN_FLIGHT_THRESHOLD_MS) {
      stats.push(
        toStat(row, {
          status: SyncJobStatus.Running,
          finishedAt: null,
          duration: null,
        }),
      );
      continue;
    }

    stats.push(toStat(row));
  }

  return {
    __typename: "EltSyncHistoryStatsResponse",
    limit: 1000,
    stats: applyFilter(stats, args.sourceStreamName),
  };
}

export function mockFetchHistoric(args: {
  from: Date;
  sourceStreamName?: string;
}): EltSyncHistoryStatsResponseFragment {
  const fromMs = args.from.getTime();
  const cutoffMs = NOW - HISTORIC_LAG_MS;

  const stats: Stat[] = [];
  for (const row of truth) {
    const startedAtMs = NOW - row.agoMin * MINUTE;
    if (startedAtMs < fromMs) continue;
    if (startedAtMs > cutoffMs) continue; // lag
    // Don't materialize the currently-running row in historic.
    if (row.status === SyncJobStatus.Running) continue;

    // Scenario A: historic captured a lagged snapshot WHILE this job was
    // still running. status=Running, finishedAt/duration null, rowsSynced=0.
    // Recent has the fresh Completed truth — recent should win.
    if (row.agoMin === SCENARIO_A_AGO_MIN) {
      stats.push(
        toStat(row, {
          status: SyncJobStatus.Running,
          finishedAt: null,
          duration: null,
          rowsSynced: 0,
          rowsSyncedMb: 0,
        }),
      );
      continue;
    }

    stats.push(toStat(row));
  }

  return {
    __typename: "EltSyncHistoryStatsResponse",
    limit: 1000,
    stats: applyFilter(stats, args.sourceStreamName),
  };
}

/** Reset call counter — handy when poking around in dev tools. */
export function resetMockTimelineBackend() {
  recentFetchCount = 0;
}

if (typeof window !== "undefined") {
  (window as any).__mockTimelineBackend = {
    reset: resetMockTimelineBackend,
    fetchRecent: mockFetchRecent,
    fetchHistoric: mockFetchHistoric,
    NOW,
  };
}
